import { ClippedHoverText } from '@components/ClippedHoverText';
import { HeadingContainer } from '@page-components/timeline/common';
import { CommonUtility } from '@utility/common';
import { POStatus } from '@utility/constants/common';
import { DateUtility } from '@utility/date';
import { Link } from 'react-router';
import { Factory, Warning } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { cn } from '@skupreme/shared/ui';
import { NormalBarRenderer } from './row';

const red = '#FF4218';
const getLabel = data => (
  <div className="sk:flex">
    <Factory size={18} />
    <Link to={`/app/scm/purchase-orders/${data._id}`}>
      <span className="sk:ml-2.5 sk:text-fg sk:underline">
        <ClippedHoverText
          name={`${data.supplier_details?.name || data.display_id}`}
        />
      </span>
    </Link>
  </div>
);

export function PurchaseOrdersSection({
  setPredictedPo,
  setFilteredPurchaseOrders,
  filteredPurchaseOrders,
  checkEntry,
  getBarColor,
  differenceInDates,
  setMaxDate,
  setMinDate,
  data,
  DayWidth,
  hidePrediction,
  ...props
}) {
  const [predictedPO, setPredictedPO] = useState(null);

  useEffect(() => {
    if (!CommonUtility.isValidObject(data)) {
      setFilteredPurchaseOrders([]);
      return;
    }

    const arr = [];

    let minDate = null;
    let maxDate = null;

    for (let i = 0; i < data.order_predictions?.length; i += 1) {
      const purchaseOrder = data.order_predictions[i];

      const daysToRestock = DateUtility.difference(
        new Date(),
        purchaseOrder.startDate,
      );
      setPredictedPO({
        ...purchaseOrder,
        name: 'Suggested PO',
        title: (
          <>
            {daysToRestock > 0 ? (
              <span>
                {CommonUtility.numberWithCommas(daysToRestock)} Days to Re-Stock
              </span>
            ) : (
              <span>Restock Today</span>
            )}
            &nbsp;
            <a
              target="_blank"
              href={`/app/scm/purchase-orders/create/?supplier_id=${data.supplier_id}`}
              rel="noreferrer"
            >
              <u>Create Purchase Order</u>
            </a>
          </>
        ),
        color: red,
        dep: purchaseOrder.provider_id,
        link: `/app/scm/purchase-orders/create/?supplier_id=${data.supplier_id}`,
      });
      setPredictedPo(purchaseOrder);
      if (!minDate) {
        minDate = purchaseOrder.startDate;
        maxDate = purchaseOrder.endDate;
      }

      // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'DateType'.
      if (DateUtility.isDateAfter(minDate, purchaseOrder.startDate))
        minDate = purchaseOrder.startDate;

      // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'DateType'.
      if (DateUtility.isDateAfter(purchaseOrder.endDate, maxDate)) {
        maxDate = purchaseOrder.endDate;
      }
    }

    for (let i = 0; i < data.purchase_orders?.length; i += 1) {
      let optEndDate = data.purchase_orders[i].endDate;
      if (data.purchase_orders[i].status !== POStatus.completed) {
        if (DateUtility.isDateAfter(new Date(), new Date(optEndDate))) {
          optEndDate = DateUtility.addHours(new Date(), 1);
          data.purchase_orders[i].title = (
            <div className="sk:flex sk:items-center">
              <span className="sk:mr-6">
                {data.purchase_orders[i]?.display_id}
              </span>
              <div className="sk:ml-6 sk:flex sk:items-center">
                <Warning className="sk:mr-1" size={18} /> Purchase order overdue
              </div>
            </div>
          );
          data.purchase_orders[i].uniqueColor = red;
          data.purchase_orders[i].siblings = [
            {
              startDate: new Date(),
              endDate: DateUtility.addDays(new Date(), 10),
              title: (
                <div className="sk:flex sk:items-center">
                  <Warning className="sk:mr-1" size={18} /> Purchase order
                  overdue
                </div>
              ),
              className: 'sk:bg-transparent! sk:text-danger!',
            },
          ];
        }
      }

      let purchaseOrder = checkEntry({
        ...data.purchase_orders[i],
        endDate: optEndDate,
      });

      if (!purchaseOrder) continue;

      if (purchaseOrder.status === 'prediction') {
        continue;
      }

      const daysDayToShip = DateUtility.difference(
        new Date(),
        purchaseOrder.endDate,
      );

      purchaseOrder = {
        title: (
          <div>
            <span className="sk:mr-6">
              {data.purchase_orders[i]?.display_id}
            </span>
            <span className="sk:ml-6">
              {daysDayToShip > 0
                ? `${CommonUtility.numberWithCommas(
                    daysDayToShip,
                  )} Days Until Shipped`
                : 'Less Than a Day Until Shipped'}
            </span>
          </div>
        ),
        ...purchaseOrder,
        name: `PO #${purchaseOrder.display_id}`,
        color: getBarColor(purchaseOrder._id),
        link: `/app/scm/purchase-orders/${purchaseOrder._id}`,
        labelRenderer: getLabel(purchaseOrder),
      };

      if (data.purchase_orders[i].status === POStatus.completed) {
        purchaseOrder.title = (
          <div>
            <span className="sk:mr-6">
              {data.purchase_orders[i]?.display_id}
            </span>
            <span className="sk:ml-6">Completed</span>
          </div>
        );
      }

      if (!minDate) {
        minDate = purchaseOrder.startDate;
        maxDate = purchaseOrder.endDate;
      }

      const findDependentShipment = data.predicted_shipments?.find(
        shipment => shipment.po_display_id === purchaseOrder.display_id,
      );
      if (purchaseOrder.endConnect || findDependentShipment) {
        purchaseOrder.dep =
          findDependentShipment?.po_display_id || purchaseOrder.endConnect;
      }

      // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'DateType'.
      if (DateUtility.isDateAfter(minDate, purchaseOrder.startDate))
        minDate = purchaseOrder.startDate;

      // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'DateType'.
      if (DateUtility.isDateAfter(purchaseOrder.endDate, maxDate)) {
        maxDate = purchaseOrder.endDate;
      }
      // @ts-expect-error - TS2345: Argument of type 'any' is not assignable to parameter of type 'never'.
      arr.push(purchaseOrder);
    }
    setMinDate(minDate);
    setFilteredPurchaseOrders(arr);
    setMaxDate(maxDate);
  }, [data]);

  return (
    <>
      {predictedPO && !hidePrediction && (
        // @ts-expect-error - TS2739: Type '{ purchaseOrders: any[]; differenceInDates: any; name: any; labelStyles: {...
        <NormalBarRenderer
          differenceInDates={differenceInDates}
          // @ts-expect-error - TS2339: Property 'name' does not exist on type 'never'.
          name={predictedPO?.name}
          labelStyles={{
            maxHeight: '98px',
            minHeight: '98px',
            marginTop: '-40px',
            paddingTop: '40px',
            marginBottom: '-10px',
          }}
          index={0}
          context="PO"
          getBarColor={getBarColor}
          data={predictedPO}
          last={false}
          additionalHeight={10}
          DayWidth={DayWidth}
          {...props}
          purchaseOrders={[...filteredPurchaseOrders, predictedPO]}
        />
      )}
      <div className="sk:relative sk:w-0">
        <div
          className="sk:pointer-events-none sk:relative sk:z-123 sk:-mt-10"
          style={{ width: differenceInDates * DayWidth + 208 }}
        >
          {(!predictedPO || hidePrediction) &&
          filteredPurchaseOrders?.length < 1 ? (
            <div className="sk:bg-surface" />
          ) : (
            <HeadingContainer
              className={cn(
                'sk:flex sk:items-center',
                predictedPO && !hidePrediction && 'sk:bg-transparent!',
              )}
            >
              <h3
                className={cn(
                  'sk:m-0 sk:ml-6',
                  predictedPO && !hidePrediction && 'sk:-mt-[70px]',
                )}
              >
                Suppliers
              </h3>
            </HeadingContainer>
          )}
        </div>
        {filteredPurchaseOrders?.map((purchaseOrder, i) => (
          // @ts-expect-error - TS2739: Type '{ differenceInDates: any; name: any; index: any; context: string; getBarCo...
          <NormalBarRenderer
            key={purchaseOrder?._id || `po-${i}`}
            differenceInDates={differenceInDates}
            name={purchaseOrder?.name}
            index={i}
            context="PO"
            getBarColor={getBarColor}
            data={purchaseOrder}
            last={i === filteredPurchaseOrders.length - 1 ? 1 : 0}
            purchaseOrders={filteredPurchaseOrders}
            DayWidth={DayWidth}
            {...props}
          />
        ))}
      </div>
    </>
  );
}
